import React from 'react';
import Icon from '../../../components/AppIcon';

const WorkerAssignmentPanel = ({ workers, taskAssignments, shifts }) => {
  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available':
        return 'bg-success/10 border-success text-success';
      case 'partial':
        return 'bg-warning/10 border-warning text-warning';
      case 'unavailable':
        return 'bg-error/10 border-error text-error';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'available':
        return 'CheckCircle2';
      case 'partial':
        return 'AlertTriangle';
      case 'unavailable':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 border-success text-success';
      case 'in_progress':
        return 'bg-primary/10 border-primary text-primary';
      case 'assigned':
        return 'bg-warning/10 border-warning text-warning';
      case 'cancelled':
        return 'bg-error/10 border-error text-error';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getWorkerTasks = (workerId) => {
    return taskAssignments?.filter(task => task?.worker_id === workerId) || [];
  };

  const getShiftName = (shiftId) => {
    const shift = shifts?.find(s => s?.id === shiftId);
    return shift?.name || 'Unknown Shift';
  };

  return (
    <div className="bg-card rounded-xl border-2 border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
            <Icon name="Users" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              Worker Assignments
            </h2>
            <p className="text-sm text-muted-foreground">
              Real-time worker availability and task status
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-success/10 border border-success">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs font-medium text-success">Live</span>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4">
        {workers?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground mt-3">
              No workers available
            </p>
          </div>
        ) : (
          workers?.map((worker) => {
            const workerTasks = getWorkerTasks(worker?.id);
            const utilizationPercent = worker?.capacity > 0 ? Math.round((worker?.assigned / worker?.capacity) * 100) : 0;

            return (
              <div
                key={worker?.id}
                className="bg-background rounded-lg border-2 border-border p-3 md:p-4 hover:border-primary transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-semibold text-sm flex-shrink-0">
                      {worker?.avatar || worker?.name?.substring(0, 2)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-semibold text-foreground truncate">
                        {worker?.name}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {worker?.role}
                      </p>
                      {worker?.skills && worker?.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {worker?.skills?.slice(0, 3)?.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-md bg-muted text-muted-foreground border border-border"
                            >
                              {skill}
                            </span>
                          ))}
                          {worker?.skills?.length > 3 && (
                            <span className="px-2 py-0.5 text-xs rounded-md bg-muted text-muted-foreground border border-border">
                              +{worker?.skills?.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${getAvailabilityColor(worker?.availability)} flex-shrink-0`}>
                    <Icon
                      name={getAvailabilityIcon(worker?.availability)}
                      size={14}
                      color={worker?.availability === 'available' ? 'var(--color-success)' : worker?.availability === 'partial' ? 'var(--color-warning)' : 'var(--color-error)'}
                    />
                    <span className="text-xs font-medium capitalize">
                      {worker?.availability}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Capacity Utilization</span>
                      <span className="font-medium">{utilizationPercent}%</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          utilizationPercent >= 100
                            ? 'bg-error'
                            : utilizationPercent >= 75
                            ? 'bg-warning' :'bg-success'
                        }`}
                        style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {worker?.assigned}/{worker?.capacity} hrs
                  </div>
                </div>

                {workerTasks?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      Assigned Tasks ({workerTasks?.length})
                    </p>
                    {workerTasks?.map((task) => (
                      <div
                        key={task?.id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md bg-muted/50 border border-border"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {task?.task_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {task?.start_time} - {task?.end_time} • {getShiftName(task?.shift_id)}
                          </p>
                        </div>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium border ${getTaskStatusColor(task?.status)}`}>
                          {task?.status?.replace('_', ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkerAssignmentPanel;